import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  CustomerButton,
  CustomerDialog,
  CustomerIconButton,
  CustomerInlineStatus,
  CustomerSegmentedControl,
  CustomerSwitch,
  CustomerTextArea,
} from "./CustomerControls";
import { Icon } from "./Icon";
import "./customer-content-patterns.css";

export function CustomerBookRow({
  title,
  meta,
  to,
  cover,
}: {
  title: string;
  meta: string;
  to: string;
  cover?: ReactNode;
}) {
  return (
    <Link className="lc-book-row" to={to}>
      <span className="lc-book-row__cover" aria-hidden="true">
        {cover ?? <Icon name="book" />}
      </span>
      <span>
        <strong>{title}</strong>
        <small>{meta}</small>
      </span>
      <Icon name="arrow" />
    </Link>
  );
}

export function CustomerContributionRow({
  actor,
  action,
  time,
  media,
}: {
  actor: string;
  action: string;
  time: string;
  media?: ReactNode;
}) {
  return (
    <article className="lc-contribution-row">
      <span className="lc-contribution-row__avatar" aria-hidden="true">
        {actor.slice(0, 1)}
      </span>
      <span>
        <strong>{actor}</strong> {action}
        <small>{time}</small>
      </span>
      {media}
    </article>
  );
}

export function CustomerPromptCard({
  question,
  onTell,
  onChoose,
}: {
  question: string;
  onTell?: () => void;
  onChoose?: () => void;
}) {
  return (
    <article className="lc-prompt-card">
      <h3>{question}</h3>
      <div>
        <CustomerButton
          tone="creative"
          icon={<Icon name="mic" />}
          iconAfter={<Icon name="arrow" />}
          onClick={onTell}
        >
          Tell this story
        </CustomerButton>
        <CustomerButton tone="quiet" onClick={onChoose}>
          Choose another prompt
        </CustomerButton>
      </div>
      <p>Speak, type, or add a photograph. You can edit everything later.</p>
    </article>
  );
}

export function CustomerPromptPicker({
  open,
  onClose,
  onChoose,
  groups,
}: {
  open: boolean;
  onClose: () => void;
  onChoose: (prompt: string) => void;
  groups: Array<{ title: string; prompts: string[] }>;
}) {
  return (
    <CustomerDialog
      open={open}
      onClose={onClose}
      title="Choose a prompt"
      description="Pick one memory to begin with. You can change it at any time."
    >
      <div className="lc-prompt-picker">
        {groups.map((group) => (
          <section key={group.title} aria-labelledby={`prompt-${group.title.replace(/\W+/g, "-")}`}>
            <h3 id={`prompt-${group.title.replace(/\W+/g, "-")}`}>{group.title}</h3>
            {group.prompts.map((prompt) => (
              <button type="button" key={prompt} onClick={() => onChoose(prompt)}>
                {prompt}
                <Icon name="arrow" size={17} />
              </button>
            ))}
          </section>
        ))}
      </div>
    </CustomerDialog>
  );
}

export function CustomerToolGroup({
  title,
  icon,
  items,
}: {
  title: string;
  icon: ReactNode;
  items: Array<{ label: string; to: string; disabled?: boolean }>;
}) {
  const id = useId();
  return (
    <section className="lc-tool-group" aria-labelledby={id}>
      <header>
        {icon}
        <h3 id={id}>{title}</h3>
      </header>
      <div>
        {items.map((item) =>
          item.disabled ? (
            <span key={item.label} aria-disabled="true">
              {item.label}
              <Icon name="lock" size={16} />
            </span>
          ) : (
            <Link key={item.label} to={item.to}>
              {item.label}
              <Icon name="arrow" size={16} />
            </Link>
          ),
        )}
      </div>
    </section>
  );
}

export function CustomerSkeleton({ lines = 3 }: { lines?: number }) {
  return (
    <div className="lc-skeleton" aria-hidden="true">
      <span />
      {Array.from({ length: lines }, (_, index) => (
        <i key={index} />
      ))}
    </div>
  );
}

export function CustomerToast({
  tone = "success",
  placement = "inline",
  children,
  onDismiss,
}: {
  tone?: "success" | "error" | "info";
  placement?: "inline" | "floating";
  children: ReactNode;
  onDismiss?: () => void;
}) {
  const noticeRef = useRef<HTMLDivElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (placement !== "floating") return;
    const active = document.activeElement;
    if (active instanceof HTMLElement && !noticeRef.current?.contains(active))
      returnFocusRef.current = active;
  }, [placement, children]);
  function dismiss() {
    const restoreFocus =
      placement === "floating" && noticeRef.current?.contains(document.activeElement);
    const usable = (element: HTMLElement | null): element is HTMLElement =>
      Boolean(
        element?.isConnected &&
          element !== document.body &&
          element.tabIndex >= 0 &&
          !element.matches(":disabled") &&
          !element.closest("[hidden], [inert]") &&
          getComputedStyle(element).display !== "none" &&
          getComputedStyle(element).visibility !== "hidden",
      );
    // An async action may disable or remove its button before this notice mounts.
    // In that case resume at the preceding usable control in the same work area.
    const notice = noticeRef.current;
    const preceding =
      restoreFocus && notice
        ? [
            ...(notice.parentElement?.querySelectorAll<HTMLElement>(
              "a[href], button, input, select, textarea, [tabindex]",
            ) ?? []),
          ]
            .filter(
              (element) =>
                usable(element) &&
                !notice.contains(element) &&
                element.getClientRects().length > 0 &&
                Boolean(notice.compareDocumentPosition(element) & Node.DOCUMENT_POSITION_PRECEDING),
            )
            .at(-1)
        : undefined;
    const returnFocus = usable(returnFocusRef.current) ? returnFocusRef.current : preceding;
    onDismiss?.();
    if (restoreFocus) returnFocus?.focus({ preventScroll: true });
  }
  return (
    <div
      ref={noticeRef}
      className={`lc-toast lc-toast--${tone}${placement === "floating" ? " lc-toast--floating" : ""}`}
      role={tone === "error" ? "alert" : "status"}
      aria-atomic="true"
    >
      <Icon name={tone === "success" ? "circleCheck" : tone === "error" ? "alert" : "info"} />
      <div>{children}</div>
      {onDismiss && (
        <CustomerIconButton
          label={placement === "floating" ? "Dismiss notification" : "Dismiss"}
          onClick={dismiss}
        >
          <Icon name="close" />
        </CustomerIconButton>
      )}
    </div>
  );
}

export function CustomerStoryComposerShell({ initialValue = "" }: { initialValue?: string }) {
  const [mode, setMode] = useState<"write" | "speak" | "upload">("write");
  const [value, setValue] = useState(initialValue);
  return (
    <section className="lc-composer" aria-labelledby="composer-title">
      <header>
        <div>
          <h3 id="composer-title">Tell this memory</h3>
          <p>Use your own words. Nothing is final until you approve it.</p>
        </div>
        <span>
          <Icon name="circleCheck" size={16} /> Saved
        </span>
      </header>
      <CustomerSegmentedControl
        label="Story capture mode"
        value={mode}
        onChange={setMode}
        options={[
          { value: "write", label: "Write", icon: <Icon name="edit" size={17} /> },
          { value: "speak", label: "Speak", icon: <Icon name="mic" size={17} /> },
          { value: "upload", label: "Upload", icon: <Icon name="upload" size={17} /> },
        ]}
      />
      <CustomerTextArea
        label="Your memory"
        value={value}
        onChange={(event) => setValue(event.target.value)}
        maxCount={2500}
        description="Names, dates, and wording stay editable."
      />
      <footer>
        <CustomerButton tone="quiet" icon={<Icon name="paperclip" />}>
          Add a memory
        </CustomerButton>
        <CustomerSwitch label="Ideas as I write" />
      </footer>
    </section>
  );
}

export function CustomerStoryMirror({ observations }: { observations: string[] }) {
  return (
    <section className="lc-guidance" aria-labelledby="story-mirror-title">
      <header>
        <Icon name="eye" />
        <h3 id="story-mirror-title">What the Studio heard</h3>
      </header>
      <ul>
        {observations.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
      <button type="button">
        <Icon name="edit" size={16} /> Edit these notes
      </button>
    </section>
  );
}

export function CustomerContextQuestion({
  question,
  onKeep,
  onDismiss,
}: {
  question: string;
  onKeep?: () => void;
  onDismiss?: () => void;
}) {
  return (
    <section className="lc-context-question" aria-labelledby="context-question-title">
      <header>
        <Icon name="lightbulb" />
        <h3 id="context-question-title">One detail to consider</h3>
      </header>
      <p>{question}</p>
      <div>
        <CustomerButton tone="creative" onClick={onKeep}>
          Answer this
        </CustomerButton>
        <CustomerButton tone="quiet" onClick={onDismiss}>
          Not for this story
        </CustomerButton>
      </div>
    </section>
  );
}

export function CustomerSuggestionDiff({
  original,
  suggestion,
}: {
  original: string;
  suggestion: string;
}) {
  return (
    <section className="lc-diff" aria-labelledby="suggestion-title">
      <header>
        <h3 id="suggestion-title">Suggested refinement</h3>
        <p>Compare before choosing. Your original is preserved.</p>
      </header>
      <div>
        <del>{original}</del>
        <ins>{suggestion}</ins>
      </div>
      <footer>
        <CustomerButton tone="creative">Keep suggestion</CustomerButton>
        <CustomerButton tone="secondary">Edit it</CustomerButton>
        <CustomerButton tone="quiet">Reject</CustomerButton>
        <CustomerIconButton label="Undo">
          <Icon name="history" />
        </CustomerIconButton>
      </footer>
    </section>
  );
}

export function CustomerVisualDirectionShell({
  title,
  description,
  colors,
  selected,
}: {
  title: string;
  description: string;
  colors: string[];
  selected?: boolean;
}) {
  return (
    <article className={`lc-direction-shell${selected ? " is-selected" : ""}`}>
      <div className="lc-direction-shell__empty">
        <Icon name="image" />
        <span>Preview supplied by LC-91</span>
      </div>
      <header>
        <div>
          <h3>{title}</h3>
          <p>{description}</p>
        </div>
        <span>
          {selected && <Icon name="circleCheck" />}
          <span className="sr-only">{selected ? "Selected" : "Not selected"}</span>
        </span>
      </header>
      <div className="lc-direction-shell__palette" aria-hidden="true">
        {colors.map((color) => (
          <i key={color} style={{ backgroundColor: color }} />
        ))}
      </div>
    </article>
  );
}

export function CustomerOutlineRow({
  number,
  title,
  summary,
}: {
  number: number;
  title: string;
  summary: string;
}) {
  return (
    <article className="lc-outline-row">
      <CustomerIconButton label={`Reorder ${title}`}>
        <Icon name="grid" />
      </CustomerIconButton>
      <span>{number}</span>
      <div>
        <strong>{title}</strong>
        <small>{summary}</small>
      </div>
      <CustomerIconButton label={`Edit ${title}`}>
        <Icon name="edit" />
      </CustomerIconButton>
    </article>
  );
}

export function CustomerFocalPointShell({ label = "Set the focal point" }: { label?: string }) {
  return (
    <section className="lc-focal-shell" aria-label={label}>
      <div>
        <Icon name="image" size={32} />
        <span>Authorized image appears here</span>
        <i aria-hidden="true" />
      </div>
      <p>Move the marker to protect the most important part of the photograph when pages crop.</p>
    </section>
  );
}

export function CustomerLiveBookPreviewShell() {
  const [view, setView] = useState<"page" | "grid">("page");
  return (
    <section className="lc-preview-shell" aria-labelledby="preview-shell-title">
      <header>
        <div>
          <h3 id="preview-shell-title">Book preview</h3>
          <p>Structure only. Page imagery remains with LC-91.</p>
        </div>
        <CustomerSegmentedControl
          label="Preview view"
          value={view}
          onChange={setView}
          options={[
            { value: "page", label: "Page", icon: <Icon name="book" size={16} /> },
            { value: "grid", label: "Grid", icon: <Icon name="grid" size={16} /> },
          ]}
        />
      </header>
      <div className="lc-preview-shell__spread">
        <div>
          <Icon name="image" />
          <span>Left page media slot</span>
        </div>
        <div>
          <Icon name="book" />
          <span>Right page content slot</span>
        </div>
      </div>
      <footer>
        <CustomerIconButton label="Previous page">
          <Icon name="arrowBack" />
        </CustomerIconButton>
        <span>Pages 12–13</span>
        <div>
          <CustomerIconButton label="Zoom out">
            <span aria-hidden="true">−</span>
          </CustomerIconButton>
          <CustomerIconButton label="Zoom in">
            <Icon name="plus" />
          </CustomerIconButton>
          <CustomerIconButton label="Version history">
            <Icon name="history" />
          </CustomerIconButton>
        </div>
      </footer>
    </section>
  );
}

export function CustomerStoryPromiseShell({
  promise,
  details,
}: {
  promise: string;
  details: string[];
}) {
  return (
    <section className="lc-promise-shell" aria-labelledby="promise-title">
      <header>
        <Icon name="bookmark" />
        <div>
          <h3 id="promise-title">Story Promise</h3>
          <p>Words only in this shared seam; LC-91 owns related imagery.</p>
        </div>
        <CustomerIconButton label="Edit Story Promise">
          <Icon name="edit" />
        </CustomerIconButton>
      </header>
      <blockquote>{promise}</blockquote>
      <ul>
        {details.map((detail) => (
          <li key={detail}>{detail}</li>
        ))}
      </ul>
      <CustomerInlineStatus tone="info">
        Review this promise before final pages are approved.
      </CustomerInlineStatus>
    </section>
  );
}
